import { useMemo, useState, type FormEvent } from "react";
import { limit, orderBy } from "firebase/firestore";
import { LoaderCircle, Search, X } from "lucide-react";
import { formatClinicDate, formatClinicTime, type Patient, type Practitioner, type Service, type Slot } from "@cop/domain";
import { api, key } from "../lib/api";
import { useLive } from "../lib/data";
import { errorMessage } from "../lib/firebase";
import { SlotChooser } from "./SlotChooser";

export interface NewAppointmentPrefill {
  serviceId?: string;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string;
  requestId?: string;
}

export function NewAppointmentPanel({
  date: initialDate,
  services,
  practitioners,
  prefill,
  onClose,
  onCreated,
}: {
  date: string;
  services: Service[];
  practitioners: Practitioner[];
  prefill?: NewAppointmentPrefill;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [serviceId, setServiceId] = useState(prefill?.serviceId ?? "");
  const [practitionerId, setPractitionerId] = useState("");
  const [date, setDate] = useState(initialDate);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [mode, setMode] = useState<"search" | "new">(prefill?.contactName ? "new" : "search");
  const [q, setQ] = useState(prefill?.contactPhone ?? "");
  const [patient, setPatient] = useState<Patient | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: patients } = useLive<Patient>("patients", [orderBy("lastName"), limit(500)]);

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const digits = term.replace(/\D/g, "");
    return (patients ?? [])
      .filter((p) => `${p.firstName} ${p.lastName} ${p.email ?? ""}`.toLowerCase().includes(term) || (digits.length > 3 && (p.phone ?? "").replace(/\D/g, "").includes(digits)) || (p.documentId ?? "").includes(term))
      .slice(0, 6);
  }, [q, patients]);

  const offering = practitioners.filter((p) => !serviceId || p.serviceIds.includes(serviceId));
  const [first, ...rest] = (prefill?.contactName ?? "").split(" ");

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!slot) return setError("Elegí un horario.");
    const f = new FormData(e.currentTarget);
    const status = f.get("status") === "pending_confirmation" ? "pending_confirmation" : "confirmed";
    setBusy(true);
    setError(null);
    try {
      const hold = await api.hold({ serviceId, practitionerId: slot.practitionerId, chairId: slot.chairId, start: slot.start, idempotencyKey: key() });
      const res = await api.book({
        holdId: hold.holdId,
        patientId: mode === "search" ? patient?.id : undefined,
        contact:
          mode === "new"
            ? {
                firstName: String(f.get("firstName")),
                lastName: String(f.get("lastName")),
                phone: String(f.get("phone")),
                email: String(f.get("email") || "") || undefined,
              }
            : undefined,
        notes: String(f.get("notes") || "") || prefill?.notes || undefined,
        status,
        idempotencyKey: key(),
      });
      if (prefill?.requestId) await api.resolveRequest({ requestId: prefill.requestId, status: "booked", appointmentId: res.appointmentId });
      onCreated(res.appointmentId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="panel" aria-labelledby="new-title">
      <header className="panel-head">
        <h2 id="new-title">Nuevo turno</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Cerrar">
          <X size={18} aria-hidden />
        </button>
      </header>
      <form className="panel-body" onSubmit={submit}>
        <div className="grid-2">
          <label className="field">
            <span>Prestación</span>
            <select value={serviceId} onChange={(e) => (setServiceId(e.target.value), setSlot(null))} required>
              <option value="">Elegir…</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.durationMinutes}′
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Profesional</span>
            <select value={practitionerId} onChange={(e) => (setPractitionerId(e.target.value), setSlot(null))}>
              <option value="">Cualquiera</option>
              {offering.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {serviceId && (
          <SlotChooser serviceId={serviceId} practitionerId={practitionerId} date={date} onDate={(d) => (setDate(d), setSlot(null))} value={slot} onChange={setSlot} practitioners={practitioners} />
        )}

        <fieldset className="stack-sm">
          <legend className="label">Paciente</legend>
          <div className="segmented" role="tablist">
            <button type="button" role="tab" aria-selected={mode === "search"} onClick={() => setMode("search")}>
              Existente
            </button>
            <button type="button" role="tab" aria-selected={mode === "new"} onClick={() => setMode("new")}>
              Nuevo
            </button>
          </div>
          {mode === "search" ? (
            patient ? (
              <div className="picked">
                <div>
                  <strong>
                    {patient.firstName} {patient.lastName}
                  </strong>
                  <span className="muted small">{patient.phone ?? patient.email}</span>
                </div>
                <button type="button" className="icon-btn" onClick={() => setPatient(null)} aria-label="Quitar paciente">
                  <X size={16} aria-hidden />
                </button>
              </div>
            ) : (
              <div className="search">
                <Search size={16} aria-hidden />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, teléfono, DNI o email" aria-label="Buscar paciente" />
                {matches.length > 0 && (
                  <ul className="search-results">
                    {matches.map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => setPatient(p)}>
                          <strong>
                            {p.firstName} {p.lastName}
                          </strong>
                          <span className="muted small">{p.phone ?? p.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          ) : (
            <div className="grid-2">
              <label className="field">
                <span>Nombre</span>
                <input name="firstName" required defaultValue={first} />
              </label>
              <label className="field">
                <span>Apellido</span>
                <input name="lastName" required defaultValue={rest.join(" ")} />
              </label>
              <label className="field">
                <span>Teléfono</span>
                <input name="phone" required inputMode="tel" defaultValue={prefill?.contactPhone ?? ""} />
              </label>
              <label className="field">
                <span>Email (opcional)</span>
                <input name="email" type="email" />
              </label>
            </div>
          )}
        </fieldset>

        <label className="field">
          <span>Nota interna (opcional)</span>
          <input name="notes" defaultValue={prefill?.notes ?? ""} maxLength={500} />
        </label>
        <label className="field">
          <span>Estado inicial</span>
          <select name="status" defaultValue="confirmed">
            <option value="confirmed">Confirmado</option>
            <option value="pending_confirmation">Pendiente de confirmación</option>
          </select>
        </label>

        {slot && (
          <p className="summary-line">
            {formatClinicDate(slot.start)} · <span className="mono">{formatClinicTime(slot.start)}</span> ·{" "}
            {practitioners.find((p) => p.id === slot.practitionerId)?.displayName}
          </p>
        )}
        {error && (
          <p className="notice notice-danger" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn-primary" disabled={busy || !slot || (mode === "search" && !patient)}>
          {busy && <LoaderCircle className="spin" size={14} aria-hidden />} Agendar turno
        </button>
      </form>
    </aside>
  );
}
